import { useState } from "react";
import { useTranslation } from "react-i18next";

import i18n from "@/i18n";
import { localeFor } from "@/utils/formatters";

import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/context/auth";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from "@/components/ui/pagination";
import Button from "@/components/button";
import { RoleLink } from "@/components/role-link";
import { ShiftCardItem } from "@/components/shift-card";
import { getShifts, search } from "@/queries/api/shiftApi";
import { QueryKeys } from "@/queries/queryKeys";
import type { ShiftListResponse } from "@/schemas";
import { useRole  } from "@/utils/hooks/use-role";
import type {Role} from "@/utils/hooks/use-role";
import type { ShiftCard } from "@/utils/shift-card";
import { shiftToCard, shiftHasStarted } from "@/utils/shift-card";

import { WorkerShiftsList } from "./-components/worker-shifts-list";

const DEFAULT_PAGE_SIZE = 10;
/** Upcoming shifts are shown on one page (soonest first) so a series is never split. */
const UPCOMING_LIMIT = 200;

type ShiftsView = "upcoming" | "past";

function localIsoDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function makeShiftsQuery(
  role: Role,
  page: number,
  pageSize: number,
  view: ShiftsView = "upcoming",
) {
  return {
    queryKey: [QueryKeys.SHIFT_LIST, role, view, page, pageSize],
    queryFn: () => {
      if (role === "employer") {
        const today = new Date();
        if (view === "upcoming") {
          // Shifts ending today or later, soonest first.
          return getShifts({
            page: 0,
            page_size: UPCOMING_LIMIT,
            startDate: `${localIsoDate(today)}T00:00:00`,
            sortOrder: "asc",
          });
        }
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        // Shifts that started before today, most recent first.
        return getShifts({
          page,
          page_size: pageSize,
          endDate: `${localIsoDate(yesterday)}T23:59:59`,
          sortOrder: "desc",
        });
      } else {
        return search({ role, page, page_size: pageSize });
      }
    },
  };
}

export const Route = createFileRoute("/app/$role/_app/shifts/")({
  loader: ({ context, params }) => {
    // Workers get their own booked/pending shifts (WorkerShiftsList fetches its own data).
    if (params.role === "worker") return;
    context.queryClient.ensureQueryData(
      makeShiftsQuery(params.role as Role, 0, DEFAULT_PAGE_SIZE),
    );
  },
  staticData: { layout: { bg: "fog", boxed: true } },
  component: ShiftsPage,
});

export function ShiftsPage() {
  const role = useRole();
  return role === "worker" ? <WorkerShiftsList /> : <EmployerShiftsPage />;
}

/** Groups the days of a multi-day series (same seriesId on this page) together, in list order. */
function groupBySeries(cards: Array<ShiftCard>): Array<Array<ShiftCard>> {
  const groups: Array<Array<ShiftCard>> = [];
  const bySeries = new Map<string, Array<ShiftCard>>();
  for (const c of cards) {
    if (c.seriesId) {
      const existing = bySeries.get(c.seriesId);
      if (existing) {
        existing.push(c);
        continue;
      }
      const g = [c];
      bySeries.set(c.seriesId, g);
      groups.push(g);
    } else {
      groups.push([c]);
    }
  }
  for (const g of groups) g.sort((a, b) => (a.startDate ?? "").localeCompare(b.startDate ?? ""));
  return groups;
}

/**
 * One row for a multi-day series: the first day's card with the series' date span, expandable
 * to show each day (each day is its own shift — edited, filled and rated separately).
 */
function SeriesGroup({
  days,
  onEdit,
}: {
  days: Array<ShiftCard>;
  onEdit: (s: ShiftCard) => (() => void) | undefined;
}) {
  const { t } = useTranslation("shifts");
  const [open, setOpen] = useState(false);
  const first = days[0];
  const last = days[days.length - 1];
  const span = `${shortDate(first.startDate)} – ${shortDate(last.startDate)}`;
  return (
    <div className="col-span-2 flex flex-col gap-3">
      <ShiftCardItem
        s={{ ...first, dateLabel: t("list.seriesSpan", { span, count: days.length }) }}
        hideUser
        onClick={() => setOpen((o) => !o)}
      />
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="self-start body-small text-teal underline underline-offset-2"
      >
        {open ? t("list.hideDays") : t("list.showDays", { count: days.length })}
      </button>
      {open ? (
        <div className="grid grid-cols-2 gap-5 pl-4 border-l-2 border-fog-dark">
          {days.map((d) => (
            <ShiftCardItem key={d.id} s={d} hideUser onClick={onEdit(d)} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function shortDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(localeFor(i18n.language || "en"), {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function EmployerShiftsPage() {
  const { t } = useTranslation("shifts");
  const role = useRole();
  const navigate = useNavigate();
  const [view, setView] = useState<ShiftsView>("upcoming");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data } = useSuspenseQuery<ShiftListResponse>(
    makeShiftsQuery(role, page, pageSize, view),
  );

  const apiShifts = data.shifts;
  const total = data.total;

  const shifts: Array<ShiftCard> = apiShifts.map((s) => shiftToCard(s as any));

  const totalPages = Math.ceil(total / pageSize);

  return (
    <section className="w-[820px] max-w-full mx-auto px-5 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="heading-2">
          {role === "employer" ? t("list.myShifts") : t("list.availableShifts")}
        </h1>
      </div>
      {role === "employer" ? (
        <Button
          variant="primary"
          className="w-full sm:w-[250px]"
          size={"lg"}
          asChild
        >
          <RoleLink to={"/app/$role/shifts/new"} className="no-underline!">
            <span className="flex flex-row body! text-[16px]! justify-center w-full relative">
              {t("list.createShift")}
            </span>
          </RoleLink>
        </Button>
      ) : null}

      {role === "employer" ? (
        <div role="tablist" className="mt-6 inline-flex rounded-lg bg-white p-1 gap-1">
          {(["upcoming", "past"] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              onClick={() => {
                setView(v);
                setPage(0);
              }}
              className={`h-9 px-4 rounded-md text-sm font-semibold transition-colors ${
                view === v ? "bg-garnet text-white" : "text-garnet hover:bg-fog"
              }`}
            >
              {t(v === "upcoming" ? "list.tabUpcoming" : "list.tabPast")}
            </button>
          ))}
        </div>
      ) : null}

      {shifts.length === 0 ? (
        <Card className="p-6">
          <CardHeader>
            <CardTitle>
              {view === "upcoming" ? t("list.noUpcomingPosted") : t("list.noPastShifts")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="body-small text-garnet/70">
              {role === "employer"
                ? view === "upcoming"
                  ? t("list.noUpcomingPostedHint")
                  : t("list.noPastShiftsHint")
                : t("list.noAvailableShifts")}
            </p>
            {role === "employer" &&
              (() => {
                const { user } = useAuth();
                return user?.onboardingCompleted ? (
                  <Link
                    to="/app/$role/shifts/new"
                    params={{ role }}
                    className="underline"
                  >
                    {t("list.createNewShift")}
                  </Link>
                ) : (
                  <span className="text-garnet/60 italic">
                    {t("list.completeOnboarding")}
                  </span>
                );
              })()}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="mt-6 space-y-3 grid grid-cols-2 gap-5">
            {groupBySeries(shifts).map((group) => {
              const editHandler = (s: ShiftCard) =>
                role === "employer" && !shiftHasStarted(s.startDateTime)
                  ? () =>
                      navigate({
                        to: "/app/$role/shifts/$shiftId/edit",
                        params: { role, shiftId: s.id },
                      })
                  : undefined;
              return group.length > 1 ? (
                <SeriesGroup key={group[0].seriesId} days={group} onEdit={editHandler} />
              ) : (
                <ShiftCardItem
                  key={group[0].id}
                  s={group[0]}
                  hideUser
                  onClick={editHandler(group[0])}
                />
              );
            })}
          </div>

          {view === "past" ? (
          <div className="mt-6 flex items-center justify-between">
            <div>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      className={
                        page === 0 ? "pointer-events-none opacity-50" : ""
                      }
                    />
                  </PaginationItem>
                  {/* Page numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i).map((p) => (
                    <PaginationItem key={p}>
                      <PaginationLink
                        isActive={p === page}
                        onClick={() => setPage(p)}
                      >
                        {p + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setPage((p) => p + 1)}
                      className={
                        page >= totalPages - 1
                          ? "pointer-events-none opacity-50"
                          : ""
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm">
                {t("list.pageLabel", { page: page + 1 })}
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
                className="text-sm border rounded px-2 py-1"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
          ) : null}
        </>
      )}
    </section>
  );
}
